import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { Reiterleiste, EinsatzFluege, phaseLabel, aktuellePhase, hatPhasenleiste, type ReiterEintrag, type EinsatzFlug, useBestaetigung, EinsatzDrohnen, EinsatzBerichtFelder, EinsatzBerichtAnzeige, PhasenZeitenBlock, EinsatzTruppsBericht, BERICHT_LEER, berichtWerteAus, berichtNutzlast, istBeendetOderAbgeschlossen, type EinsatzBerichtWerte } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, useNeuParam, FilterZurueck, KennzahlenLeiste, statusKennzahlen } from '../components/ListenWerkzeuge'
import { FormSection, FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { EinsatzMedien, EinsatzExport, type MedienUebersicht } from '../components/EinsatzMedienExport'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge, statusLabel } from '../components/StatusBadge'
import { TypChip, typLabel } from '../components/Chips'
import { Statusband, stundenText } from '../components/Statusband'
import { PageHeader, Card, Button, Input, Select, Textarea } from '@pilot-hub/ui'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { missionApi, userApi, api } from '@pilot-hub/api-client'
import { FUNKTIONEN } from '@pilot-hub/config'
import 'leaflet/dist/leaflet.css'
import { MapPicker } from '../components/MapPicker'
import { EinsatzRisikobewertung } from '../components/EinsatzRisikobewertung'
import { kmZugeordnete, kmZeiten, kmMitZeiten, kmTruppText, holeKmData, type KMData, type KMZeit } from '../utils/kraefteManagement'
import { ArrowLeft, Plus, Edit, Eye, Trash2, Users, Save } from 'lucide-react'
import { DatumZeitFeld, ZeitFeld } from '../components/DatumFelder'

// Taktische Karte (nur lesend): eigener Chunk, damit Leaflet-Kartenbausteine nicht im Hauptbündel liegen
const TaktischeKarteLesen = lazy(() => import('@pilot-hub/ui/components/karte/index').then(m => ({ default: m.TaktischeKarteLesen })))

interface Einsatz {
  id: number
  titel: string
  typ: string
  phase?: string | null
  status: string
  prioritaet: string
  beginn_geplant: string | null
  ende_geplant: string | null
  alarmierungszeit: string | null
  abfahrt_wache: string | null
  ankunft_einsatzort: string | null
  einsatzende: string | null
  einsatzbereit: string | null
  einsatzleiter_typ: string | null
  einsatzleiter_nutzer_id: number | null
  einsatzleiter_funkrufname: string | null
  einsatzleiter_funkgruppe: string | null
  einsatzleiter_drohne_nutzer_id?: number | null
  einsatzleiter_drohne_funkrufname?: string | null
  einsatzleiter_name: string | null
  einsatznummer?: string | null
  einsatzort_adresse: string | null
  einsatzort_lat: number | null
  einsatzort_lon: number | null
  einsatzgebiet_radius_m: number | null
  beschreibung: string | null
  risikobewertung: string | null
  wetterbedingungen: string | null
  erstellt_am: string | null
  aktualisiert_am: string | null
  erstellt_von: number | null
  leiter_label: string | null
  teilnehmer_count: number
}

// FastAPI liefert bei 422 ein Array von Fehlerobjekten in "detail" – nie direkt rendern (React-Absturz)
function fehlerText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d)) return d.map((x: any) => `${(x?.loc ?? []).slice(1).join('.')}: ${x?.msg ?? ''}`).join('; ') || fallback
  return fallback
}

const PRIORITAET_OPTIONEN = ['KRITISCH', 'HOCH', 'NORMAL', 'NIEDRIG']
const TYP_OPTIONEN = ['EINSATZ', 'UEBUNGSEINSATZ', 'UEBUNG']

// Status-Optionen pro Einsatz-Typ
const STATUS_NACH_TYP: Record<string, string[]> = {
  EINSATZ: ['ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE', 'EINSATZBEREIT', 'ABGESCHLOSSEN'],
  UEBUNGSEINSATZ: ['GEPLANT', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE', 'EINSATZBEREIT', 'ABGESCHLOSSEN'],
  UEBUNG: ['GEPLANT', 'LAUFEND', 'BEENDET', 'ABGESCHLOSSEN'],
}

// Default-Status pro Typ
const DEFAULT_STATUS: Record<string, string> = {
  EINSATZ: 'ALARMIERUNG',
  UEBUNGSEINSATZ: 'GEPLANT',
  UEBUNG: 'GEPLANT',
}

const LEER = {
  titel: '',
  typ: 'EINSATZ',
  status: 'ALARMIERUNG',
  prioritaet: 'NORMAL',
  beginn_geplant: '',
  alarmierungszeit: '',
  abfahrt_zeit: '',
  ankunft_zeit: '',
  einsatzende_zeit: '',
  einsatzbereit_zeit: '',
  einsatzleiter_typ: 'NUTZER',
  einsatzleiter_nutzer_id: '',
  einsatzleiter_funkrufname: '',
  einsatzleiter_funkgruppe: '',
  einsatzleiter_drohne_nutzer_id: '',
  einsatzleiter_drohne_funkrufname: '',
  einsatzort_adresse: '',
  einsatzort_lat: '',
  einsatzort_lon: '',
  einsatzgebiet_radius_m: '',
  beschreibung: ''
}

type EinsatzReiter = 'uebersicht' | 'zeiten' | 'kraefte' | 'fluege' | 'medien' | 'export' | 'karte' | 'risiko'

/** Reiter, in dem ein Fehler (Feldname oder Meldungstext) liegt */
function reiterFuerFehler(feld: string): EinsatzReiter {
  if (/einsatznummer|alarmierende|stichwort|schleife|ergebnis|zeit|alarmierung|abfahrt|ankunft|einsatzende|einsatzbereit|phase/i.test(feld)) return 'zeiten'
  return 'uebersicht'
}

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; einsatzId?: number }

export function MissionsPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const navigate = useNavigate()
  const [einsaetze, setEinsaetze] = useState<Einsatz[]>([])
  const [leiter, setLeiter] = useState<Array<{ id: number | string; label: string }>>([])
  const [search, setSearch] = usePersistState('einsaetze.search', '')
  const [statusFilter, setStatusFilter] = usePersistState('einsaetze.statusFilter', 'all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [speichert, setSpeichert] = useState(false)
  const [bericht, setBericht] = useState<EinsatzBerichtWerte>({ ...BERICHT_LEER })
  const [reiter, setReiter] = useState<EinsatzReiter>('uebersicht')
  const [fehlerReiter, setFehlerReiter] = useState<EinsatzReiter | null>(null)
  const waehleReiter = (k: EinsatzReiter) => { setReiter(k); setFehlerReiter(f => (f === k ? null : f)) }

  // ---- Flüge dieses Einsatzes, Drohnen-Anzahl und Drohnen-Namen (für den Reiter-Zähler)
  const [flugDaten, setFlugDaten] = useState<{ id: number; items: EinsatzFlug[] } | null>(null)
  const [flugLaedt, setFlugLaedt] = useState(false)
  const [flugFehler, setFlugFehler] = useState<string | null>(null)
  const [drohnenNamen, setDrohnenNamen] = useState<Record<number, string> | null>(null)
  const [drohnenAnzahl, setDrohnenAnzahl] = useState(0)

  // ---- Kräfte Management: zugeteilte Einsatzkräfte dieses Einsatzes
  const [kmData, setKmData] = useState<KMData | null>(null)
  const [kmZeitenState, setKmZeitenState] = useState<Record<number, KMZeit>>({})
  const [kmLaedt, setKmLaedt] = useState(false)
  const [kmFehler, setKmFehler] = useState<string | null>(null)
  const [alleNutzer, setAlleNutzer] = useState<Array<{ id: number; name: string }>>([])

  const kraftName = useCallback((id: number) => {
    const u = alleNutzer.find(x => x.id === id)
    return u ? u.name : `Nutzer #${id}`
  }, [alleNutzer])

  const zugeordnete = useMemo(() => kmZugeordnete(kmData), [kmData])

  // Fahrzeugnamen (Funkrufname bzw. Kennzeichen) für die Anzeige der Besatzungszuordnung
  const [fahrzeugNamen, setFahrzeugNamen] = useState<Record<number, string>>({})
  useEffect(() => {
    if (zugeordnete.length === 0 || Object.keys(fahrzeugNamen).length > 0) return
    api.get('/fahrzeuge', { params: { size: 500 } }).then(r => {
      const m: Record<number, string> = {}
      for (const f of ((r.data as any)?.items ?? []) as any[]) {
        m[f.id] = String(f.rufzeichen || f.kennzeichen || f.fahrzeugtyp || f.typ || `Fahrzeug #${f.id}`)
      }
      setFahrzeugNamen(m)
    }).catch(() => {})
  }, [zugeordnete.length, fahrzeugNamen])

  const geaendert = useUngespeichert([form, bericht], JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [e, l, alle] = await Promise.all([
        missionApi.list({ size: 500 }),
        userApi.list({ size: 500, rolle: 'FUEHRUNG' }),
        userApi.list({ size: 500 })
      ])
      setEinsaetze((e.data as any).items ?? [])
      setAlleNutzer(((alle.data as any).items ?? []).map((u: any) => ({
        id: u.id,
        name: `${u.vorname ?? ''} ${u.nachname ?? ''}`.trim() || u.benutzername
      })))
      const fuehrungskraefte = ((l.data as any).items ?? []).map((u: any) => ({
        id: u.id,
        label: `${[u.vorname, u.nachname].filter(Boolean).join(' ') || u.benutzername} (${u.benutzername})`
      }))
      setLeiter([
        { id: 'FEUERWEHR', label: 'Feuerwehr' },
        { id: 'POLIZEI', label: 'Polizei' },
        { id: 'THW', label: 'THW' },
        { id: 'DLRG', label: 'DLRG' },
        { id: 'ELW', label: 'ELW' },
        ...fuehrungskraefte.map((u: any) => ({ id: u.id, label: u.label }))
      ])
    } catch (err: any) {
      setFehler(fehlerText(err, 'Einsätze konnten nicht geladen werden'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])
  useNeuParam(oeffneNeu, !loading)

  // Direkter Weg aus dem Dashboard (z. B. Ergebnis eintragen): /missions?bearbeiten=<id> öffnet den Einsatz im Reiter „Zeiten & Bericht“
  const [suchParams, setSuchParams] = useSearchParams()
  useEffect(() => {
    const oeffnenId = Number(suchParams.get('oeffnen'))
    if (oeffnenId && !loading) {
      const ez = einsaetze.find(x => x.id === oeffnenId)
      if (ez) oeffneDetail(ez)
      else setFehler('Der Einsatz wurde nicht gefunden.')
      setSuchParams({}, { replace: true })
      return
    }
    const id = Number(suchParams.get('bearbeiten'))
    if (!id || loading) return
    const e = einsaetze.find(x => x.id === id)
    if (e) oeffneBearbeiten(e, 'zeiten')
    else setFehler('Der Einsatz wurde nicht gefunden.')
    setSuchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, einsaetze, suchParams])

  // Zugeordnete Einsatzkräfte (Kräfte Management) laden, sobald ein Einsatz geöffnet wird.
  // Retry: kurz nach einem Token-Refresh (401) oder Container-Neustart (502) klappt es erneut.
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setKmData(null); setKmZeitenState({}); setKmFehler(null); return }
    let cancelled = false
    setKmLaedt(true); setKmFehler(null)
    ;(async () => {
      for (let versuch = 0; versuch < 3; versuch++) {
        try {
          const r = await api.get(`/kraefte-management/${id}`)
          if (cancelled) return
          const d: KMData = holeKmData(r)
          setKmData(d); setKmZeitenState(kmZeiten(d)); setKmFehler(null)
          break
        } catch {
          if (cancelled) return
          if (versuch === 2) {
            setKmData(null); setKmZeitenState({})
            setKmFehler('Einsatzkräfte konnten nicht geladen werden.')
          } else {
            await new Promise(res => setTimeout(res, 600))
          }
        }
      }
      if (!cancelled) setKmLaedt(false)
    })()
    return () => { cancelled = true }
  }, [view.einsatzId])

  // Flüge des Einsatzes (lesend); beim Öffnen des Reiters „Flüge“ erneut laden
  const fluegeOffen = reiter === 'fluege' || reiter === 'export'
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setFlugDaten(null); setFlugFehler(null); return }
    let cancelled = false
    setFlugLaedt(true)
    api.get('/fluege', { params: { einsatz_id: id, size: 500 } })
      .then(r => { if (!cancelled) { setFlugDaten({ id, items: (r.data as any)?.items ?? [] }); setFlugFehler(null) } })
      .catch(() => { if (!cancelled) setFlugFehler('Flüge konnten nicht geladen werden.') })
      .finally(() => { if (!cancelled) setFlugLaedt(false) })
    return () => { cancelled = true }
  }, [view.einsatzId, fluegeOffen])
  // Fotos des Einsatzes (Zähler im Reiter „Medien“, ZIP-Download)
  const [medienDaten, setMedienDaten] = useState<MedienUebersicht | null>(null)
  const [medienLaedt, setMedienLaedt] = useState(false)
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setMedienDaten(null); return }
    let cancelled = false
    setMedienLaedt(true)
    api.get('/dji/medien/galerie/fotos', { params: { einsatz_id: id } })
      .then(r => {
        const liste: any[] = Array.isArray(r.data) ? r.data : ((r.data as any)?.fotos ?? (r.data as any)?.items ?? [])
        if (!cancelled) setMedienDaten({ id, fotos: liste.length, ir: liste.filter(f => f.typ === 'ir').length, ids: liste.map(f => f.id) })
      })
      .catch(() => { if (!cancelled) setMedienDaten({ id, fotos: 0, ir: 0, ids: [] }) })
      .finally(() => { if (!cancelled) setMedienLaedt(false) })
    return () => { cancelled = true }
  }, [view.einsatzId])
  const medien = medienDaten && medienDaten.id === view.einsatzId ? medienDaten : null
  const fluege = flugDaten && flugDaten.id === view.einsatzId ? flugDaten.items : []

  // Drohnen-Namen einmalig, sobald Flüge angezeigt werden
  useEffect(() => {
    if (!fluegeOffen || drohnenNamen) return
    api.get('/drohnen', { params: { size: 500 } }).then(r => {
      const m: Record<number, string> = {}
      for (const x of ((r.data as any)?.items ?? []) as any[]) {
        m[x.id] = [x.asb_inventarnummer, [x.hersteller, x.modell].filter(Boolean).join(' ')].filter(Boolean).join(' – ') || x.kennzeichen || `Drohne #${x.id}`
      }
      setDrohnenNamen(m)
    }).catch(() => setDrohnenNamen({}))
  }, [fluegeOffen, drohnenNamen])

  // Anzahl der Drohnen im Einsatz (Zähler im Reiter „Kräfte & Drohnen“)
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setDrohnenAnzahl(0); return }
    let cancelled = false
    api.get(`/einsaetze/${id}/drohnen`)
      .then(r => { if (!cancelled) setDrohnenAnzahl(Array.isArray(r.data) ? r.data.length : 0) })
      .catch(() => { if (!cancelled) setDrohnenAnzahl(0) })
    return () => { cancelled = true }
  }, [view.einsatzId, reiter])


  const einsatzleiterLabel = (e: Einsatz) => {
    if (!e.einsatzleiter_typ) return '—'
    if (e.einsatzleiter_typ === 'NUTZER' && e.einsatzleiter_nutzer_id) {
      const u = leiter.find(x => x.id === e.einsatzleiter_nutzer_id)
      return u ? u.label : `Nutzer #${e.einsatzleiter_nutzer_id}`
    }
    const labels: Record<string, string> = { FEUERWEHR: 'Feuerwehr', POLIZEI: 'Polizei', THW: 'THW', DLRG: 'DLRG', ELW: 'ELW', NUTZER: 'Nutzer' }
    return labels[e.einsatzleiter_typ] || e.einsatzleiter_typ
  }

  function oeffneNeu() {
    setReiter('uebersicht'); setFehlerReiter(null); setFehler(null)
    setForm({ ...LEER, status: DEFAULT_STATUS[LEER.typ], prioritaet: 'NORMAL', typ: 'EINSATZ', einsatzleiter_typ: 'NUTZER' })
    setBericht({ ...BERICHT_LEER })
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(e: Einsatz, ziel?: EinsatzReiter) {
    // Wechsel Ansicht -> Bearbeiten desselben Einsatzes behält den Reiter, sonst „Übersicht“ (oder das Ziel)
    setReiter(ziel ?? (view.mode === 'detail' && view.einsatzId === e.id ? reiter : 'uebersicht')); setFehlerReiter(null)
    setForm({
      titel: e.titel ?? '',
      typ: e.typ ?? 'EINSATZ',
      status: e.status ?? DEFAULT_STATUS[e.typ ?? 'EINSATZ'],
      prioritaet: e.prioritaet ?? 'NORMAL',
      beginn_geplant: e.beginn_geplant ? e.beginn_geplant.slice(0, 16) : '',
      alarmierungszeit: e.alarmierungszeit ? e.alarmierungszeit.slice(0, 16) : '',
      abfahrt_zeit: e.abfahrt_wache ? e.abfahrt_wache.slice(0, 16) : '',
      ankunft_zeit: e.ankunft_einsatzort ? e.ankunft_einsatzort.slice(0, 16) : '',
      einsatzende_zeit: e.einsatzende ? e.einsatzende.slice(0, 16) : '',
      einsatzbereit_zeit: e.einsatzbereit ? e.einsatzbereit.slice(0, 16) : '',
      einsatzleiter_typ: e.einsatzleiter_typ ?? 'NUTZER',
      einsatzleiter_nutzer_id: e.einsatzleiter_nutzer_id ? String(e.einsatzleiter_nutzer_id) : '',
      einsatzleiter_funkrufname: e.einsatzleiter_funkrufname ?? '',
      einsatzleiter_funkgruppe: e.einsatzleiter_funkgruppe ?? '',
      einsatzleiter_drohne_nutzer_id: e.einsatzleiter_drohne_nutzer_id ? String(e.einsatzleiter_drohne_nutzer_id) : '',
      einsatzleiter_drohne_funkrufname: e.einsatzleiter_drohne_funkrufname ?? '',
      einsatzort_adresse: e.einsatzort_adresse ?? '',
      einsatzort_lat: e.einsatzort_lat ? String(e.einsatzort_lat) : '',
      einsatzort_lon: e.einsatzort_lon ? String(e.einsatzort_lon) : '',
      einsatzgebiet_radius_m: e.einsatzgebiet_radius_m ? String(e.einsatzgebiet_radius_m) : '',
      beschreibung: e.beschreibung ?? ''
    })
    setBericht(berichtWerteAus(e))
    setView({ mode: 'edit', einsatzId: e.id })
  }

  function oeffneDetail(e: Einsatz) {
    setReiter('uebersicht'); setFehlerReiter(null)
    setView({ mode: 'detail', einsatzId: e.id })
    setForm({
      titel: e.titel,
      typ: e.typ,
      status: e.status,
      prioritaet: e.prioritaet,
      einsatzleiter_typ: e.einsatzleiter_typ || 'NUTZER',
      einsatzleiter_nutzer_id: String(e.einsatzleiter_nutzer_id ?? ''),
      einsatzleiter_funkrufname: e.einsatzleiter_funkrufname || '',
      einsatzleiter_funkgruppe: e.einsatzleiter_funkgruppe || '',
      einsatzleiter_drohne_nutzer_id: e.einsatzleiter_drohne_nutzer_id ? String(e.einsatzleiter_drohne_nutzer_id) : '',
      einsatzleiter_drohne_funkrufname: e.einsatzleiter_drohne_funkrufname ?? '',
      einsatzleiter_name: e.einsatzleiter_name || '',
      einsatzort_adresse: e.einsatzort_adresse || '',
      einsatzort_lat: String(e.einsatzort_lat ?? ''),
      einsatzort_lon: String(e.einsatzort_lon ?? ''),
      einsatzgebiet_radius_m: String(e.einsatzgebiet_radius_m ?? ''),
      beschreibung: e.beschreibung || '',
      risikobewertung: e.risikobewertung || '',
      wetterbedingungen: e.wetterbedingungen || '',
      alarmierungszeit: e.alarmierungszeit ? e.alarmierungszeit.slice(0, 16) : '',
      abfahrt_zeit: e.abfahrt_wache ? e.abfahrt_wache.slice(0, 16) : '',
      ankunft_zeit: e.ankunft_einsatzort ? e.ankunft_einsatzort.slice(0, 16) : '',
      einsatzende_zeit: e.einsatzende ? e.einsatzende.slice(0, 16) : '',
      einsatzbereit_zeit: e.einsatzbereit ? e.einsatzbereit.slice(0, 16) : '',
      beginn_geplant: e.beginn_geplant ? e.beginn_geplant.slice(0, 16) : '',
      ende_geplant: e.ende_geplant ? e.ende_geplant.slice(0, 16) : '',
    })
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ ...LEER, status: DEFAULT_STATUS[LEER.typ], prioritaet: 'NORMAL', typ: 'EINSATZ', einsatzleiter_typ: 'NUTZER' })
  }

  async function speichern() {
    if (!form.titel.trim()) { setFehler('Bitte einen Titel angeben (Reiter „Übersicht“).'); setFehlerReiter('uebersicht'); setReiter('uebersicht'); return }
    setFehler(null); setFehlerReiter(null)
    // Beenden/Abschließen ohne Ergebnis: nur ein weicher Hinweis (kein Pflichtfeld, später nachtragbar)
    const vorher = view.einsatzId ? einsaetze.find(x => x.id === view.einsatzId) : null
    if (istBeendetOderAbgeschlossen(form.status) && !istBeendetOderAbgeschlossen(vorher?.status) && !bericht.ergebnis) {
      const ok = await bestaetigen({
        titel: 'Ohne Ergebnis abschließen?',
        text: 'Für diesen Einsatz ist noch kein Ergebnis eingetragen (z. B. Gefunden, Fehlalarm).',
        hinweis: 'Das Ergebnis ist freiwillig und lässt sich auch nach dem Abschluss eintragen.',
        bestaetigenText: 'Trotzdem speichern', abbrechenText: 'Zurück', gefahr: false,
      })
      if (!ok) return
    }
    setSpeichert(true)
    try {
      const payload: any = {
        ...berichtNutzlast(bericht),
        titel: form.titel,
        typ: form.typ,
        status: form.status,
        prioritaet: form.prioritaet,
        beginn_geplant: form.beginn_geplant || null,
        alarmierungszeit: form.alarmierungszeit || null,
        abfahrt_zeit: form.abfahrt_zeit || null,
        ankunft_zeit: form.ankunft_zeit || null,
        einsatzende_zeit: form.einsatzende_zeit || null,
        einsatzbereit_zeit: form.einsatzbereit_zeit || null,
        einsatzleiter_typ: form.einsatzleiter_typ,
        einsatzleiter_nutzer_id: form.einsatzleiter_nutzer_id ? Number(form.einsatzleiter_nutzer_id) : null,
        einsatzleiter_funkrufname: form.einsatzleiter_funkrufname || null,
        einsatzleiter_funkgruppe: form.einsatzleiter_funkgruppe || null,
        einsatzleiter_drohne_nutzer_id: form.einsatzleiter_drohne_nutzer_id ? Number(form.einsatzleiter_drohne_nutzer_id) : null,
        einsatzleiter_drohne_funkrufname: form.einsatzleiter_drohne_funkrufname || null,
        einsatzort_adresse: form.einsatzort_adresse || null,
        // einsatzort_lat/lon ermittelt die API aus dem Einsatzort (Koordinaten oder Adresse)
        einsatzgebiet_radius_m: form.einsatzgebiet_radius_m ? Number(form.einsatzgebiet_radius_m) : null,
        beschreibung: form.beschreibung,
        risikobewertung: form.risikobewertung,
        wetterbedingungen: form.wetterbedingungen
      }
      if (view.mode === 'edit' && view.einsatzId) await missionApi.update(view.einsatzId, payload)
      else await missionApi.create(payload)
      // Einsatzzeiten der zugeteilten Kräfte (Kräfte Management) mitspeichern
      if (view.mode === 'edit' && view.einsatzId && zugeordnete.length > 0) {
        await api.put(`/kraefte-management/${view.einsatzId}`, { data: kmMitZeiten(kmData, kmZeitenState) })
      }
      zurueckZurListe()
      await laden()
    } catch (err: any) {
      const text = fehlerText(err, 'Speichern fehlgeschlagen')
      setFehler(text)
      const d = err?.response?.data?.detail
      const feld = Array.isArray(d) ? String(d[0]?.loc?.slice(-1)[0] ?? '') : text
      const ziel = reiterFuerFehler(feld)
      setFehlerReiter(ziel); setReiter(ziel)
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await missionApi.delete(id)
      await laden()
    } catch (err: any) {
      setFehler(fehlerText(err, 'Löschen fehlgeschlagen'))
    }
  }

  // Dynamische Status-Optionen basierend auf ausgewähltem Typ
  const aktuelleStatusOptionen = STATUS_NACH_TYP[form.typ] || STATUS_NACH_TYP.EINSATZ

  const gefiltert = einsaetze.filter(e =>
    (statusFilter === 'all' || e.status === statusFilter) &&
    (e.titel.toLowerCase().includes(search.toLowerCase()) ||
     (e.typ ?? "").toLowerCase().includes(search.toLowerCase()) ||
     typLabel(e.typ).toLowerCase().includes(search.toLowerCase()))
  )

  const columns: SpalteDef[] = [
    { key: 'titel', header: 'Titel', accessor: 'titel' },
    { key: 'typ', header: 'Typ', accessor: 'typ', sortWert: (e: any) => typLabel(e.typ), render: (v: any) => <TypChip typ={v} /> },
    { key: 'status', header: 'Status', accessor: 'status', sortWert: (e: any) => statusLabel(e.status), render: (v: any) => <StatusBadge wert={v} /> },
    { key: 'prioritaet', header: 'Priorität', accessor: 'prioritaet', sortWert: (e: any) => ({ KRITISCH: 0, HOCH: 1, NORMAL: 2, NIEDRIG: 3 } as Record<string, number>)[e.prioritaet] ?? 4, render: (v: any) => <StatusBadge wert={v} /> },
    { key: 'beginn', header: 'Geplant', accessor: 'beginn_geplant', sortWert: (e: any) => e.beginn_geplant ? new Date(e.beginn_geplant).getTime() : null, render: (v: any) => v ? new Date(v).toLocaleString('de-DE') : '—' },
    { key: 'leiter', header: 'Einsatzleitung', accessor: 'id', mobile: 'hidden', sortWert: (e: any) => einsatzleiterLabel(e), render: (_v: any, e: any) => einsatzleiterLabel(e) },
    { key: 'ort', header: 'Ort', accessor: 'einsatzort_adresse', mobile: 'meta', render: (_v: any, e: any) => e.einsatzort_adresse || '—' },
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
        <ZeilenAktionen name={String(row.titel)} onBearbeiten={() => oeffneBearbeiten(row)} onLoeschen={async () => { const name = row.titel; if (await bestaetigen({ titel: 'Einsatz löschen?', text: 'Einsatz', objekt: String(name), hinweis: 'Alle Flüge dieses Einsatzes und die Teilnehmerzuordnung werden mit gelöscht (inkl. Telemetrie, Checklisten und Kräfte-Management). Das kann nicht rückgängig gemacht werden.' })) missionApi.delete(row.id).then(laden) }} />
      )
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          subtitle="Alle Einsätze der ASB Drohnenstaffel verwalten"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neuer Einsatz</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
            <Input placeholder="Titel oder Typ" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-72" />
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              options={[{ value: 'all', label: 'Alle Status' }, ...Object.values(STATUS_NACH_TYP).flat().filter((v, i, a) => a.indexOf(v) === i).map(s => ({ value: s, label: statusLabel(s) }))]}
              placeholder="— Status —" className="w-full sm:w-48" />
            <FilterZurueck aktiv={!!search || statusFilter !== 'all'} onClick={() => { setSearch(''); setStatusFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {einsaetze.length}</div>
          </div>
          <div className="mb-4"><KennzahlenLeiste items={statusKennzahlen(einsaetze.map(e => e.status), statusFilter, setStatusFilter, 'Alle Einsätze')} /></div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Einsätze…</div> : (
            <ListenTabelle persistKey="einsaetze" data={gefiltert} columns={columns} keyAccessor={(e: any) => String(e.id)} onRowClick={oeffneDetail} emptyMessage="Keine Einsätze vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW (Reiter) ======
  const currentEinsatz = view.einsatzId ? einsaetze.find(e => e.id === view.einsatzId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  const kopfBeginn = form.beginn_geplant
    ? new Date(form.beginn_geplant).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'noch nicht festgelegt'
  const beginnLabel = ['LAUFEND', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT'].includes(form.status) ? 'Einsatzbeginn' : 'Geplant am'
  const kraefteAnzahl = zugeordnete.length + drohnenAnzahl
  const phaseAnzeige = hatPhasenleiste(form.typ) ? (phaseLabel(aktuellePhase({ typ: form.typ, status: form.status, phase: currentEinsatz?.phase }), form.typ) || null) : null
  const reiterListe: Array<ReiterEintrag<EinsatzReiter>> = ([
    { key: 'uebersicht', label: 'Übersicht' },
    { key: 'zeiten', label: 'Zeiten & Bericht' },
    { key: 'kraefte', label: isCreate ? 'Kräfte & Drohnen' : `Kräfte & Drohnen (${kraefteAnzahl})` },
    ...(!isCreate ? [
      { key: 'fluege' as const, label: `Flüge (${fluege.length})` },
      { key: 'medien' as const, label: `Medien (${medien?.fotos ?? 0})` },
      { key: 'export' as const, label: 'Bericht & Export' },
      { key: 'karte' as const, label: 'Taktische Karte' },
      { key: 'risiko' as const, label: 'Risikobewertung' },
    ] : []),
  ] as Array<ReiterEintrag<EinsatzReiter>>).map(r => ({ ...r, fehler: fehlerReiter === r.key }))
  // Optionen mit aktuellem Wert auch dann, wenn die Person nicht in der Liste der Führungskräfte steht
  const leiterOptionen = (wert: string) => {
    const o = leiter.filter(l => typeof l.id === 'number').map(u => ({ value: String(u.id), label: u.label }))
    if (wert && !o.some(x => x.value === wert)) o.push({ value: wert, label: kraftName(Number(wert)) })
    return o
  }

  return (
    <div className="space-y-4">
      {dialog}
      {/* Kopfzeile: immer sichtbar */}
      <div className="z-20 space-y-3 bg-dark-bg pb-2 md:sticky md:top-0" data-testid="einsatz-kopf">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentEinsatz && <Button data-testid="einsatz-bearbeiten" onClick={() => oeffneBearbeiten(currentEinsatz)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
            <span className="text-sm text-dark-text-muted">{isCreate ? 'Neuer Einsatz' : isEdit ? 'Einsatz bearbeiten' : 'Einsatz anzeigen'}</span>
          </div>
          <div className="flex items-center gap-2">
          </div>
        </div>
        <Card className="!py-3">
          <h1 className="break-words text-xl font-bold text-dark-text" data-testid="kopf-titel">{form.titel || (isCreate ? 'Neuer Einsatz' : '—')}</h1>
          <Statusband
            chips={<>
              <TypChip typ={form.typ} />
              {phaseAnzeige ? <span className="inline-flex items-center rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-0.5 text-xs font-semibold text-sky-200">{phaseAnzeige}</span> : <StatusBadge wert={form.status} />}
              <StatusBadge wert={form.prioritaet} />
            </>}
            zaehler={isCreate ? undefined : [
              { label: 'Flüge', wert: fluege.length }, { label: 'Flugstunden', wert: stundenText(fluege) },
              { label: 'Medien', wert: medien?.fotos ?? 0 }, { label: 'Kräfte', wert: kraefteAnzahl, hinweis: 'zugeteilte Einsatzkräfte und Drohnen' },
            ]}
            angaben={<>
              <span>{beginnLabel}: <span className="text-dark-text">{kopfBeginn}</span></span>
              {form.einsatzort_adresse ? <span className="min-w-0 break-words">Einsatzort: <span className="text-dark-text">{form.einsatzort_adresse}</span></span> : null}
            </>}
          />
        </Card>
        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200" data-testid="einsatz-fehler">{fehler}</div>}
        <Reiterleiste reiter={reiterListe} aktiv={reiter} onWechsel={waehleReiter} testid="einsatz-reiter" />
      </div>

      <Card>
        <div className="space-y-4">
          {reiter === 'uebersicht' && (
            <>
              <div>
                <label className="label">Titel *</label>
                <Input value={form.titel} onChange={e => setForm({...form, titel: e.target.value})} disabled={isDetail} placeholder="z.B. Waldbrand Eberstadt" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="label">Typ *</label>
                  <Select value={form.typ} onChange={e => { const val = e.target.value; setForm({...form, typ: val, status: DEFAULT_STATUS[val] })}} disabled={isDetail}
                    options={TYP_OPTIONEN.map(t => ({ value: t, label: typLabel(t) }))} placeholder="— Typ wählen —" />
                </div>
                <div>
                  <label className="label">Status *</label>
                  <Select value={form.status} onChange={e => setForm({...form, status: e.target.value})} disabled={isDetail}
                    options={aktuelleStatusOptionen.map(s => ({ value: s, label: statusLabel(s) }))} placeholder="— Status wählen —" />
                </div>
                <div>
                  <label className="label">Priorität *</label>
                  <Select value={form.prioritaet} onChange={e => setForm({...form, prioritaet: e.target.value})} disabled={isDetail}
                    options={PRIORITAET_OPTIONEN.map(p => ({ value: p, label: statusLabel(p) }))} placeholder="— Priorität —" />
                </div>
                <div>
                  <label className="label">{beginnLabel}</label>
                  <DatumZeitFeld  value={form.beginn_geplant} onChange={v => setForm({...form, beginn_geplant: v})} disabled={isDetail} className="w-full" />
                </div>
              </div>
              <div>
                <label className="label">Einsatzort</label>
                <MapPicker
            value={form.einsatzort_adresse}
            onChange={val => setForm({...form, einsatzort_adresse: val})}
            lat={currentEinsatz?.einsatzort_lat}
            lon={currentEinsatz?.einsatzort_lon}
            placeholder="GPS-Koordinaten, Adresse oder Karten-Pin"
            disabled={isDetail}
          />
              </div>
              <div>
                <label className="label">Beschreibung</label>
                <Textarea value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} disabled={isDetail} rows={3} />
              </div>
              <FormSection titel="Einsatzleitung" beschreibung="Leitung der Einsatzstelle und Einsatzleiter Drohne (EGRED 2)" spalten={1}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="min-w-0">
                    <label className="label">Einsatzleitung</label>
                    <Select value={form.einsatzleiter_typ} onChange={e => { const val = e.target.value; setForm({...form, einsatzleiter_typ: val, einsatzleiter_nutzer_id: '' })}} disabled={isDetail}
                      options={[
                        { value: 'FEUERWEHR', label: 'Feuerwehr' },
                        { value: 'POLIZEI', label: 'Polizei' },
                        { value: 'THW', label: 'THW' },
                        { value: 'DLRG', label: 'DLRG' },
                        { value: 'ELW', label: 'ELW' },
                        { value: 'NUTZER', label: 'Führungskraft (aus Liste)' }
                      ]} placeholder="— Einsatzleitung wählen —" />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkrufname der Einsatzleitung</label>
                    <Input value={form.einsatzleiter_funkrufname} onChange={e => setForm({...form, einsatzleiter_funkrufname: e.target.value})} disabled={isDetail} placeholder="z.B. Florian 10/1, ELW 1, etc." />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkgruppe</label>
                    <Input value={form.einsatzleiter_funkgruppe} onChange={e => setForm({...form, einsatzleiter_funkgruppe: e.target.value})} disabled={isDetail} placeholder="z.B. 4m, 2m, Kanal 1, etc." />
                  </div>
                </div>
                {form.einsatzleiter_typ === 'NUTZER' && (
                  <div>
                    <label className="label">Führungskraft</label>
                    <Select value={form.einsatzleiter_nutzer_id} onChange={e => setForm({...form, einsatzleiter_nutzer_id: e.target.value})} disabled={isDetail}
                      options={leiterOptionen(form.einsatzleiter_nutzer_id)}
                      placeholder="— Führungskraft wählen —" />
                  </div>
                )}
                {/* EGRED 2: Einsatzleiter Drohne */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="min-w-0">
                    <label className="label">Einsatzleiter Drohne (Gruppenführer / Abschnittsleiter)</label>
                    <Select value={form.einsatzleiter_drohne_nutzer_id} onChange={e => setForm({...form, einsatzleiter_drohne_nutzer_id: e.target.value})} disabled={isDetail}
                      options={leiterOptionen(form.einsatzleiter_drohne_nutzer_id)}
                      placeholder="— Führungskraft wählen —" />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkrufname Einsatzleiter Drohne</label>
                    <Input value={form.einsatzleiter_drohne_funkrufname} onChange={e => setForm({...form, einsatzleiter_drohne_funkrufname: e.target.value})} disabled={isDetail} placeholder="z.B. Drohne 1, UAS-Führer" />
                  </div>
                </div>
              </FormSection>
            </>
          )}

          {reiter === 'zeiten' && (
            <>
              {isDetail && currentEinsatz && (
                <>
                  {currentEinsatz.typ && <PhasenZeitenBlock einsatz={currentEinsatz} api={api} onGeaendert={() => { void laden() }} />}
                  <div className="border-t border-dark-border pt-4">
                    <h3 className="mb-2 text-sm font-semibold text-dark-text">Angaben für den Einsatzbericht</h3>
                    <dl className="space-y-2 text-sm"><EinsatzBerichtAnzeige einsatz={currentEinsatz} /></dl>
                  </div>
                  <EinsatzTruppsBericht einsatzId={currentEinsatz.id} einsatzName={currentEinsatz.titel} einsatznummer={currentEinsatz.einsatznummer} api={api} />
                </>
              )}
              {!isDetail && (
                <>
                  {form.typ === 'EINSATZ' && (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                      <div className="min-w-0">
                        <label className="label">Alarmierungszeit</label>
                        <DatumZeitFeld  value={form.alarmierungszeit} onChange={v => setForm({...form, alarmierungszeit: v})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Abfahrt Wache</label>
                        <DatumZeitFeld  value={form.abfahrt_zeit} onChange={v => setForm({...form, abfahrt_zeit: v})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Ankunft Einsatzort</label>
                        <DatumZeitFeld  value={form.ankunft_zeit} onChange={v => setForm({...form, ankunft_zeit: v})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Einsatzende</label>
                        <DatumZeitFeld  value={form.einsatzende_zeit} onChange={v => setForm({...form, einsatzende_zeit: v})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Einsatzbereitschaft wiederhergestellt</label>
                        <DatumZeitFeld  value={form.einsatzbereit_zeit} onChange={v => setForm({...form, einsatzbereit_zeit: v})} className="w-full" />
                      </div>
                    </div>
                  )}
                  {form.typ !== 'EINSATZ' && <p className="text-xs text-dark-text-subtle">Phasenzeiten gibt es nur bei Einsätzen; bei Übungen sind sie nur lesend in der Ansicht sichtbar.</p>}
                  <EinsatzBerichtFelder werte={bericht} onChange={setBericht} />
                </>
              )}
            </>
          )}

          {reiter === 'kraefte' && (
            <>
              {/* Drohnen im Einsatz (Zuordnung, keine Pflicht) */}
              {!isCreate && currentEinsatz && <EinsatzDrohnen einsatzId={currentEinsatz.id} api={api} bearbeitbar={isEdit} />}

              {/* Zugeordnete Einsatzkräfte aus dem Kräfte Management */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-dark-text">
                  <Users className="h-4 w-4 text-primary-400" /> Zugeordnete Einsatzkräfte
                </h3>
                {kmLaedt ? (
                  <p className="mt-2 text-xs text-dark-text-muted">Lade Kräfte…</p>
                ) : kmFehler ? (
                  <p className="mt-2 text-xs text-red-300">{kmFehler}</p>
                ) : zugeordnete.length === 0 ? (
                  <p className="mt-2 text-xs text-dark-text-subtle">
                    Keine Einsatzkräfte zugeteilt. Die Zuordnung erfolgt im Kräfte Management.
                  </p>
                ) : (
                  <div className="mt-3 overflow-hidden rounded-xl border border-dark-border">
                    <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(96px,150px)_minmax(96px,150px)] gap-2 bg-dark-bg-tertiary px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-dark-text-subtle">
                      <span>Einsatzkraft</span><span>Einsatzbeginn</span><span>Einsatzende</span>
                    </div>
                    {zugeordnete.map(z => {
                      const zt = kmZeitenState[z.nutzerId] ?? { beginn: '', ende: '' }
                      const setz = (feld: 'beginn' | 'ende', wert: string) => setKmZeitenState(prev => ({
                        ...prev,
                        [z.nutzerId]: { ...(prev[z.nutzerId] ?? { beginn: '', ende: '' }), [feld]: wert },
                      }))
                      return (
                        <div key={z.nutzerId} className={`grid grid-cols-[minmax(0,1.6fr)_minmax(96px,150px)_minmax(96px,150px)] items-center gap-2 border-t border-dark-border px-3 py-2${z.bereich === 'einsatzende' && zt.ende ? ' bg-dark-bg-tertiary/40' : ''}`}>
                          <div className="min-w-0">
                            <div className="truncate text-sm text-dark-text">{kraftName(z.nutzerId)}</div>
                            <div className="text-[10px] text-dark-text-subtle break-words">
                              {z.bereich === 'besatzung' ? (z.fahrzeugId ? `Fahrzeugbesatzung · ${fahrzeugNamen[z.fahrzeugId] ?? `Fahrzeug #${z.fahrzeugId}`}` : 'Fahrzeugbesatzung') + (kmTruppText(kmData, z.nutzerId) ? ` · ${kmTruppText(kmData, z.nutzerId)}` : '')
                                : z.bereich === 'reserve' ? 'Reserve Wache'
                                : z.bereich === 'einsatzende' ? (zt.ende ? 'Einsatzende – beendet' : 'Einsatzende')
                                : 'Einsatzreserve'}
                            </div>
                          </div>
                          <ZeitFeld  value={zt.beginn} disabled={isDetail} onChange={v => setz('beginn', v)} />
                          <ZeitFeld  value={zt.ende} disabled={isDetail} onChange={v => setz('ende', v)} />
                        </div>
                      )
                    })}
                  </div>
                )}
                {!isDetail && !kmFehler && zugeordnete.length > 0 && (
                  <p className="mt-2 text-xs text-dark-text-subtle">
                    Zeiten können erfasst oder geändert werden – sie werden mit dem Kräfte Management geteilt.
                  </p>
                )}
              </div>
            </>
          )}

          {reiter === 'fluege' && !isCreate && currentEinsatz && (
            <EinsatzFluege fluege={fluege} laedt={flugLaedt} fehler={flugFehler}
              drohneLabel={id => (id ? (drohnenNamen?.[id] ?? `Drohne #${id}`) : '—')}
              pilotLabel={id => (id ? kraftName(id) : '—')}
              onOeffnen={f => navigate(`/flights?oeffnen=${f.id}`)} />
          )}

          {reiter === 'medien' && !isCreate && currentEinsatz && (
            <EinsatzMedien einsatzId={currentEinsatz.id} titel={currentEinsatz.titel} medien={medien} laedt={medienLaedt} />
          )}

          {reiter === 'export' && !isCreate && currentEinsatz && (
            <EinsatzExport einsatzId={currentEinsatz.id} titel={currentEinsatz.titel} nummer={currentEinsatz.einsatznummer} fluege={fluege} medien={medien}
              flugLabel={(f: any) => `${f.flug_nr ? f.flug_nr + ' · ' : ''}${f.startzeit ? new Date(f.startzeit).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'} · ${drohnenNamen?.[f.drohne_id] ?? 'Drohne'} · ${f.pilot_id ? kraftName(f.pilot_id) : '—'}`} />
          )}

          {reiter === 'karte' && !isCreate && currentEinsatz && (
            <Suspense fallback={<p className="text-sm text-dark-text-muted">Taktische Karte wird geladen …</p>}>
              <TaktischeKarteLesen einsatz={currentEinsatz} api={api} funktionLabel={v => FUNKTIONEN.find(f => f.value === v)?.label} />
            </Suspense>
          )}

          {reiter === 'risiko' && !isCreate && currentEinsatz && (
            <EinsatzRisikobewertung einsatzId={currentEinsatz.id} listenPfad={`/einsaetze/${currentEinsatz.id}/risikobewertungen`} />
          )}
        </div>
      </Card>
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={false} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}
